import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaChevronDown,
  FaExclamationTriangle,
  FaFolderOpen,
  FaPrint,
  FaQuestionCircle,
  FaRegLightbulb,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScollToTop";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://api.monappliclub.com";
const FAQ_API_BASE =
  process.env.REACT_APP_FAQ_API_URL || "https://api.monappliclub.com/api/faqs";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const isActiveFaq = (faq) => {
  if (
    faq?.is_active === undefined ||
    faq?.is_active === null ||
    faq?.is_active === ""
  ) {
    return true;
  }

  return faq.is_active === true || faq.is_active === 1 || faq.is_active === "1";
};

const getCategory = (faq) => {
  const category = String(faq?.categorie || "").trim();
  return category || "Autres";
};

const sanitizeHtml = (html = "") => {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
};

const FaqSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-48 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 h-16 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-slate-800" />

        <div className="mt-10 space-y-5">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-28 animate-pulse rounded-[1.7rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const EmptyState = ({ searchTerm }) => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaQuestionCircle />
      </div>

      <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        {searchTerm ? "Aucune question trouvée" : "Aucune FAQ disponible"}
      </h2>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        {searchTerm
          ? "Essayez avec un autre mot-clé ou effacez la recherche pour afficher toutes les questions."
          : "Les questions fréquentes apparaîtront ici dès qu’elles seront ajoutées."}
      </p>
    </div>
  );
};

const ErrorState = ({ message }) => {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label }) => {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
      <Icon className="mx-auto mb-3 text-2xl text-primary" />

      <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>
    </div>
  );
};

const CategoryPill = ({ category, count, active, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] transition",
        active
          ? "border-primary bg-primary text-white shadow-lg"
          : "border-slate-200 bg-white/80 text-slate-600 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
      )}
    >
      {category}
      <span
        className={cn(
          "rounded-full px-2 py-0.5 text-[10px]",
          active
            ? "bg-white/20 text-white"
            : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300",
        )}
      >
        {count}
      </span>
    </button>
  );
};

const FaqItem = ({ faq, open, onToggle }) => {
  const safeHtml = useMemo(() => sanitizeHtml(faq?.contenu || ""), [faq]);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[1.7rem] border shadow-sm backdrop-blur-xl transition",
        open
          ? "border-primary/30 bg-white dark:border-primary/30 dark:bg-slate-900"
          : "border-slate-200 bg-white/80 hover:border-primary/25 dark:border-white/10 dark:bg-white/5",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 p-5 text-left sm:p-6"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-start gap-4">
          <div
            className={cn(
              "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition",
              open
                ? "bg-primary text-white"
                : "bg-primary/10 text-primary dark:bg-primary/20",
            )}
          >
            <FaQuestionCircle />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
              {getCategory(faq)}
            </p>

            <h3 className="mt-2 font-barlow text-lg font-bold leading-snug text-slate-950 dark:text-white sm:text-xl">
              {faq?.titre || "Question"}
            </h3>
          </div>
        </div>

        <span
          className={cn(
            "mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition",
            open
              ? "rotate-180 border-primary bg-primary text-white"
              : "border-slate-200 bg-white text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
          )}
        >
          <FaChevronDown className="text-sm" />
        </span>
      </button>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-slate-200 px-5 py-5 dark:border-white/10 sm:px-6">
            <div
              className={cn(
                "prose prose-slate max-w-none dark:prose-invert",
                "text-sm leading-7 text-slate-600 dark:text-slate-300",
                "prose-a:font-bold prose-a:text-primary",
                "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                "dark:[&_strong]:font-bold dark:[&_b]:font-bold",
              )}
              dangerouslySetInnerHTML={{
                __html: safeHtml,
              }}
            />
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className={cn(
          "h-1.5 w-full bg-gradient-to-r from-transparent via-primary to-transparent transition",
          open ? "opacity-90" : "opacity-0",
        )}
      />
    </div>
  );
};

const FaqPage = () => {
  useScrollToTop();

  const navigate = useNavigate();

  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Toutes");
  const [openFaqId, setOpenFaqId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchFaqs = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const response = await axios.get(`${FAQ_API_BASE}/allFaqs`);

        const rawData = response.data;

        const allFaqs = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.faqs)
            ? rawData.faqs
            : Array.isArray(rawData?.items)
              ? rawData.items
              : Array.isArray(rawData?.data)
                ? rawData.data
                : [];

        if (!mounted) return;

        const activeFaqs = allFaqs.filter(isActiveFaq);

        setFaqs(activeFaqs);
        setOpenFaqId(activeFaqs[0]?.id || null);
      } catch (error) {
        console.error("Erreur lors du chargement des FAQ :", error);
        console.error("URL appelée :", `${FAQ_API_BASE}/allFaqs`);

        if (mounted) {
          setErrorMsg("Impossible de charger les questions fréquentes.");
          toast.error("Erreur lors du chargement des FAQ.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchFaqs();

    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(() => {
    const counts = faqs.reduce((acc, faq) => {
      const category = getCategory(faq);
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});

    return [
      {
        name: "Toutes",
        count: faqs.length,
      },
      ...Object.entries(counts).map(([name, count]) => ({
        name,
        count,
      })),
    ];
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    const query = normalize(searchTerm);

    return faqs.filter((faq) => {
      const matchesCategory =
        activeCategory === "Toutes" || getCategory(faq) === activeCategory;

      if (!matchesCategory) return false;

      if (!query) return true;

      const haystack = normalize(
        `${faq?.titre || ""} ${faq?.contenu || ""} ${faq?.categorie || ""}`,
      );

      return haystack.includes(query);
    });
  }, [faqs, searchTerm, activeCategory]);

  const groupedFaqs = useMemo(() => {
    return filteredFaqs.reduce((acc, faq) => {
      const category = getCategory(faq);

      if (!acc[category]) acc[category] = [];
      acc[category].push(faq);

      return acc;
    }, {});
  }, [filteredFaqs]);

  const totalCategories = Math.max(categories.length - 1, 0);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setOpenFaqId(null);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setOpenFaqId(filteredFaqs[0]?.id || null);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setOpenFaqId(null);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <FaqSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <style>{`
        @media print {
          nav,
          footer,
          .no-print {
            display: none !important;
          }

          main {
            padding-top: 0 !important;
            background: #ffffff !important;
          }

          .print-card {
            box-shadow: none !important;
            border: none !important;
          }

          .faq-answer-print {
            display: block !important;
          }
        }
      `}</style>

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
          >
            <FaPrint className="text-sm" />
            Imprimer
          </button>
        </div>

        <section className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaRegLightbulb />
            Questions fréquentes
          </div>

          <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Besoin <span className="text-primary">d’aide ?</span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les réponses aux questions les plus fréquentes concernant
            le club, les inscriptions, les cours et l’utilisation du site.
          </p>
        </section>

        {errorMsg && (
          <div className="mt-10">
            <ErrorState message={errorMsg} />
          </div>
        )}

        {!errorMsg && (
          <>
            <section className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3">
              <StatCard
                icon={FaQuestionCircle}
                value={faqs.length}
                label="Questions"
              />

              <StatCard
                icon={FaFolderOpen}
                value={totalCategories}
                label="Catégories"
              />

              <StatCard
                icon={FaSearch}
                value={filteredFaqs.length}
                label="Résultats"
              />
            </section>

            <section className="print-card mt-10 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-7 lg:p-8">
              <div className="no-print mb-7 rounded-[2rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5 sm:p-5">
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Rechercher une question, un mot-clé..."
                    className="h-14 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-12 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950/60 dark:text-white dark:placeholder:text-slate-500"
                  />

                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-4 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary dark:hover:bg-white/10"
                      aria-label="Effacer la recherche"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <CategoryPill
                      key={category.name}
                      category={category.name}
                      count={category.count}
                      active={activeCategory === category.name}
                      onClick={() => handleCategoryClick(category.name)}
                    />
                  ))}
                </div>
              </div>

              {filteredFaqs.length === 0 ? (
                <EmptyState searchTerm={searchTerm} />
              ) : (
                <div className="space-y-8">
                  {Object.entries(groupedFaqs).map(([category, questions]) => (
                    <section key={category}>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
                          <FaFolderOpen />
                        </div>

                        <div>
                          <h2 className="font-barlow text-2xl font-bold uppercase tracking-tight text-slate-950 dark:text-white">
                            {category}
                          </h2>

                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {questions.length} question
                            {questions.length > 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {questions.map((faq) => (
                          <FaqItem
                            key={faq.id}
                            faq={faq}
                            open={openFaqId === faq.id}
                            onToggle={() =>
                              setOpenFaqId((current) =>
                                current === faq.id ? null : faq.id,
                              )
                            }
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}

              <div
                aria-hidden
                className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
              />
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default FaqPage;
